import { useMemo, useRef } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { AgGridReact } from 'ag-grid-react';
import { useReviewees } from 'reducers/reviewCreationSlice';
import { addPluralSuffixS } from 'utils/helpers/stringHelpers';

import noEmployeesView from 'assets/assetsV1/illustrations/newIllustrations/noEmployeesView.svg';

import { Accordion, AccordionButton } from 'uiToolkitV1/Accordion';
import { AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { Avatar } from 'uiToolkitV1/Avatar';
import { Divider } from 'uiToolkitV1/Divider';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useGetViewEmployees } from '../../Hooks/dataHooks/revieweesDataHooks';

const EmployeeRenderer = ({ data }) => (
  <ListItemComp
    title={data.employee?.displayName}
    subTitle={data.employee?.email}
    LeftComponent={() => <Avatar src={data.employee?.profilePhoto} />}
    isBoldText={false}
    withoutBackground
  />
);

const DesignationRenderer = ({ data }) => {
  const value = data?.designation ? data.designation : '—';
  return <TextBodyMedium>{value}</TextBodyMedium>;
};

const DepartmentRenderer = ({ data }) => {
  const value = data?.department ? data.department : '—';

  return <TextBodyMedium>{value}</TextBodyMedium>;
};

const StyledBox = styled(Flex)`
  justify-content: center;
  align-items: center;
  height: ${(props) => (props.showEmptyScreen ? '224px' : 'auto')};
  border: 1px solid ${(props) => props.theme.colors.neutral.EBEFF3};
  padding: 12px;
  padding-bottom: ${(props) => (props.showEmptyScreen ? '0' : '12px')};
  margin-left: 26px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: ${(props) => (props.showEmptyScreen ? props.theme.colors.surface.FDFDFD : 'white')};
`;

const ViewEmployeesAgGrid = ({ id, idx, name, views }) => {
  const { data, isLoading, isRefetching, isError, isSuccess } = useGetViewEmployees({ viewId: id });

  const gridRef = useRef();

  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  const columnDefs = useMemo(
    () => [
      {
        field: 'employee',
        headerName: 'Employee',
        cellRenderer: EmployeeRenderer,

        filter: 'agTextColumnFilter',
        filterValueGetter: ({ data }) => `${data?.employee?.displayName} ${data?.employee?.email}`,

        sortable: true,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        valueGetter: ({ data }) => data.employee?.displayName ?? '',
      },
      {
        field: 'department',
        headerName: 'Department',
        headerTooltip: 'Department',
        valueFormatter: ({ value }) => {
          if (value) return value;
          return '-';
        },
        filter: 'agSetColumnFilter',
        cellRenderer: DepartmentRenderer,
      },
      {
        field: 'designation',
        headerName: 'Designation',
        headerTooltip: 'Designation',
        valueFormatter: ({ value }) => {
          if (value) return value;
          return '-';
        },
        filter: 'agSetColumnFilter',
        cellRenderer: DesignationRenderer,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      cellStyle: { height: '100%', display: 'flex', alignItems: 'center', overflow: 'hidden' },
      flex: 1,
      headerComponentParams: {
        enableMenu: true,
      },
    }),
    []
  );

  if (isLoading || isRefetching) {
    return (
      <div style={{ marginTop: '16px' }}>
        <SkeletonLoader />
      </div>
    );
  }

  return (
    <AccordionItem2 key={id} sxProps={{ borderBottom: idx === views.length - 1 ? 'none !important' : '1px solid' }}>
      <AccordionButton>
        <TextBodyMedium>
          {name} ({data?.length ?? 0} Employee{addPluralSuffixS(data?.length ?? 0)})
        </TextBodyMedium>
      </AccordionButton>
      <AccordionPanel2>
        <StyledBox showEmptyScreen={isSuccess && data.length === 0}>
          {isSuccess && data.length === 0 && (
            <EmptyScreen
              heading="There are no employees in this view"
              illustrationSrc={noEmployeesView}
              illustrationStyle={{ width: '140px' }}
              isSmallTextSize
            />
          )}
          {data.length > 0 && (
            <div style={gridStyle} className="ag-theme-alpine ag-theme-views-custom-styled">
              <AgGridReact
                ref={gridRef}
                defaultColDef={defaultColDef}
                columnDefs={columnDefs}
                rowData={data}
                animateRows
                pagination
                paginationPageSize={5}
                cacheQuickFilter
                rowHeight={60}
                className={`views${id}`}
                domLayout="autoHeight"
              />
            </div>
          )}
        </StyledBox>
      </AccordionPanel2>
    </AccordionItem2>
  );
};

export const ViewEmployeeList = () => {
  const views = useReviewees();

  return (
    <>
      <Divider mt={6} />
      <Accordion allowMultiple allowToggle boxShadow="none !important">
        {views.map(({ id, name, employees }, idx) => (
          <ViewEmployeesAgGrid id={id} idx={idx} name={name} views={views} />
        ))}
      </Accordion>
    </>
  );
};
